/**
 * Maison Grès — experiences page form → cart → checkout.
 */
(function () {
  function setFormBusy(form, busy) {
    form.querySelectorAll('input, select, button').forEach((el) => {
      if (busy) {
        el.setAttribute('aria-disabled', 'true');
        if (el.tagName !== 'BUTTON') el.disabled = true;
      } else {
        el.removeAttribute('aria-disabled');
        if (el.tagName !== 'BUTTON') el.disabled = false;
      }
    });
  }

  function setSubmitLoading(btn, form, loading) {
    if (!btn) return;
    if (!btn.dataset.labelDefault) {
      btn.dataset.labelDefault = btn.textContent.trim();
    }
    const loadingLabel = form.getAttribute('data-loading-label') || 'Loading…';

    if (loading) {
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = loadingLabel;
      setFormBusy(form, true);
    } else {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.textContent = btn.dataset.labelDefault;
      setFormBusy(form, false);
      updateExperienceSubmitState(form, btn);
    }
  }

  function updateExperienceSubmitState(form, submitBtn) {
    if (!submitBtn || submitBtn.classList.contains('is-loading')) return;

    const experienceSelect = form.querySelector('[data-maison-experience-type]');
    const option = experienceSelect?.selectedOptions?.[0];
    const experience = experienceSelect?.value || '';
    const date = form.querySelector('[data-maison-experience-date]')?.value || '';
    const name = form.querySelector('[data-maison-experience-name]')?.value?.trim() || '';
    const phone = form.querySelector('[data-maison-experience-phone]')?.value?.trim() || '';
    const email = form.querySelector('[data-maison-experience-email]')?.value?.trim() || '';
    const guestsInput = form.querySelector('[data-maison-experience-guests]');
    const isDayPass = option?.getAttribute('data-booking-mode') === 'day_pass';
    const guestsOk = !isDayPass || Boolean(guestsInput?.value);
    const variantId = option && date ? resolveVariantId(option, date) : '';

    const complete = Boolean(experience && date && name && phone && email && guestsOk && variantId);
    submitBtn.disabled = !complete;
    submitBtn.setAttribute('aria-disabled', complete ? 'false' : 'true');
  }

  function isWeekend(dateStr) {
    if (!dateStr) return false;
    const day = new Date(`${dateStr}T12:00:00`).getDay();
    return day === 0 || day === 6;
  }

  function resolveVariantId(option, dateStr) {
    const mode = option.getAttribute('data-booking-mode');
    if (mode === 'day_pass') {
      return isWeekend(dateStr)
        ? option.getAttribute('data-variant-weekend')
        : option.getAttribute('data-variant-weekday');
    }
    return option.getAttribute('data-variant-id');
  }

  function resolveQuantity(option, guests) {
    const mode = option.getAttribute('data-booking-mode');
    if (mode === 'day_pass') {
      return Math.max(1, Number(guests) || 1);
    }
    return 1;
  }

  function initExperienceForms(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-maison-experience-checkout]').forEach((form) => {
      if (form.dataset.maisonExperienceReady === 'true') return;
      form.dataset.maisonExperienceReady = 'true';

      const errorEl = form.querySelector('[data-maison-experience-error]');
      const submitBtn =
        form.querySelector('[data-maison-experience-submit]') || form.querySelector('[type="submit"]');
      const experienceSelect = form.querySelector('[data-maison-experience-type]');
      const guestsField = form.querySelector('[data-maison-experience-guests-wrap]');
      const guestsInput = form.querySelector('[data-maison-experience-guests]');
      const priceHint = form.querySelector('[data-maison-experience-price-hint]');

      function updateGuestsVisibility() {
        const option = experienceSelect?.selectedOptions?.[0];
        const mode = option?.getAttribute('data-booking-mode');
        const isDayPass = mode === 'day_pass';
        if (guestsField) guestsField.hidden = !isDayPass;
        if (guestsInput) guestsInput.required = isDayPass;
        if (priceHint && option) {
          priceHint.textContent = option.getAttribute('data-price-hint') || '';
        }
        updateExperienceSubmitState(form, submitBtn);
      }

      experienceSelect?.addEventListener('change', updateGuestsVisibility);
      updateGuestsVisibility();

      const syncSubmit = () => updateExperienceSubmitState(form, submitBtn);
      form.addEventListener('input', syncSubmit);
      form.addEventListener('change', syncSubmit);
      syncSubmit();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitBtn?.classList.contains('is-loading') || submitBtn?.disabled) return;

        if (!form.checkValidity()) {
          form.reportValidity();
          syncSubmit();
          return;
        }

        const option = experienceSelect?.selectedOptions?.[0];
        const date = form.querySelector('[data-maison-experience-date]')?.value || '';
        const name = form.querySelector('[data-maison-experience-name]')?.value?.trim() || '';
        const phone = form.querySelector('[data-maison-experience-phone]')?.value?.trim() || '';
        const email = form.querySelector('[data-maison-experience-email]')?.value?.trim() || '';
        const guests = form.querySelector('[data-maison-experience-guests]')?.value || '1';
        const experience = experienceSelect?.value || '';
        const variantId = resolveVariantId(option, date);
        const quantity = resolveQuantity(option, guests);
        const redirect = form.getAttribute('data-redirect') || '/checkout';

        if (!name || !phone || !email) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Please fill in your name, phone number, and email.';
          }
          syncSubmit();
          return;
        }

        if (!variantId) {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent =
              form.getAttribute('data-error-experience') || 'Please choose an experience.';
          }
          experienceSelect?.focus();
          return;
        }

        if (errorEl) errorEl.hidden = true;
        setSubmitLoading(submitBtn, form, true);

        const properties = {
          Date: date,
          Guests: `${guests} guests`,
          Name: name,
          Phone: phone,
          Email: email,
          Experience: experience
        };

        if (option?.getAttribute('data-booking-mode') === 'day_pass') {
          properties['Entrance fee'] = isWeekend(date)
            ? 'USD 20/person (weekend / public holiday)'
            : 'USD 15/person (weekday)';
        }

        try {
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin'
          });

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity,
                  properties
                }
              ]
            })
          });

          if (!addRes.ok) {
            const err = await addRes.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Could not start checkout');
          }

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              attributes: {
                'Experience booking name': name,
                'Experience booking phone': phone,
                'Experience booking email': email,
                'Experience booking guests': guests,
                'Experience booking date': date,
                'Experience type': experience
              }
            })
          });

          window.location.href = redirect;
        } catch (e) {
          console.error('Maison experience checkout failed', e);
          setSubmitLoading(submitBtn, form, false);
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent =
              e.message ||
              form.getAttribute('data-error-generic') ||
              'Something went wrong. Please try again.';
          }
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initExperienceForms());
  } else {
    initExperienceForms();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initExperienceForms(event.target);
  });
})();
